import DSBox from '../../../../design-system/DSBox';
import useCreateCollection from '../../hooks/useCreateCollection';
import useAppContext from '../../../../hooks/useAppContext';
import { DSTypography } from '../../../../design-system/Typography';
import CollectionStatus from '../CollectionStatus';
import DSTabGradientViewPaper from '../../../../design-system/Paper/DSTabGradientViewPaper';
import { useTheme } from '@mui/material';
import SelectKlydosModal from './SelectKlydosModal';
import DeleteDialog from '../../../../components/DeleteDialog';
import { DELETE_COLLECTION_ALERT } from '../../../../consts/alerts';
import ErrorDialog from '../../../../components/ErrorDialog';
import WarningDialog from '../../../../components/WarningDialog';
import DSCircularProgress from '../../../../design-system/DSCircularProgress';
import { CollectionReviewStates } from '../../../../api/collections';
import AffiliateLinkButton from '../AffiliateLinkButton';
import CollectionContentTab from './Tabs/CollectionContentTab';
import CollectionAssetsTab from './Tabs/CollectionAssetsTab';
import CollectionMonetizationTab from './Tabs/CollectionMonetizationTab';
import DSPrimaryButton from '../../../../design-system/DSPrimaryButton';
import DSTabButton from '../../../../design-system/DSTabButton';
import DSSecondaryButton from '../../../../design-system/DSSecondaryButton';
import DSDivider from '../../../../design-system/DSDivider';
import DSTooltip from '../../../../design-system/DSTooltip';
import { ReactComponent as EyeIcon } from '../../../../design-system/Icons/EyeIcon.svg';
import { ReactComponent as MarketplaceIcon } from '../../../../design-system/Icons/marketplace.svg';

const CreateCollectionContainer = () => {
  const { spacing } = useTheme();
  const { user } = useAppContext();

  const {
    handleCoverUpload,
    handleLogoUpload,
    handleLogotypeUpload,
    handleThumbnailBackgroundUpload,
    handleCoverRemove,
    handleLogoRemove,
    handleLogotypeRemove,
    handleThumbnailBackgroundRemove,
    handlePublishClick,
    isPickModalOpen,
    handleOpenPickModal,
    handleClosePickModal,
    handleRemoveKlydo,
    handlePickKlydos,
    isSubmitDisabled,
    handleMissingFields,
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
    warningDialogOpen,
    reviewComment,
    setReviewComment,
    reviewMessage,
    handleWarningConfirm,
    handleWarningClose,
    isDeleting,
    deletedCollectionId,
    setDeletedCollectionId,
    handleDeleteCollection,
    isLoading,
    collectionName,
    setCollectionName,
    collectionDescription,
    setCollectionDescription,
    collectionCover,
    collectionLogo,
    collectionLogotype,
    collectionThumbnailBackground,
    isUploadingCover,
    isUploadingLogo,
    isUploadingLogotype,
    isUploadingThumbnailBackground,
    collectionKlydos,
    updatedAt,
    handleKlydoCardClick,
    collectionId,
    handleCreateShopifyPreviewUrl,
    isFetchingPreviewUrl,
    isInMarketplace,
    hasAffiliateLink,
    bundleCartImage,
    activeTab,
    handleTabChange,
    collectionLabels,
    handleAddLabel,
    handleRemoveLabel,
    isCollectionHoldPendulumLogo,
    setIsCollectionHoldPendulumLogo,
    collectionPendulumLogoSize,
    setCollectionPendulumLogoSize,
    collectionPendulumLogoType,
    setCollectionPendulumLogoType,
    isApprovedCollection,
    collectionThumbnailContrastColor,
    setCollectionThumbnailContrastColor,
    canPreviewProductPage,
    selectedPlanId,
    setSelectedPlanId,
    handleOrderChange,
    collectionMockTemplateId,
    setCollectionMockTemplateId,
    collectionThumbnailKlydos,
    setCollectionThumbnailKlydos,
    collectionCredits,
    setCollectionCredits,
    isNameTaken,
    warningMessage,
    nameWarningNote,
  } = useCreateCollection();

  const isMissingArtistName = !user?.name || user.name.trim() === '';

  if (isLoading) {
    return (
      <DSTabGradientViewPaper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          marginY: spacing(3),
          paddingY: spacing(4),
          minHeight: '86vh',
          gap: spacing(3),
        }}
      >
        <DSCircularProgress />
      </DSTabGradientViewPaper>
    );
  }

  return (
    <DSBox>
      <DSTabGradientViewPaper
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          minHeight: '86vh',
          marginY: spacing(3),
          paddingY: spacing(4),
          gap: spacing(3),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing(1),
              flex: 1,
              minWidth: 0,
            }}
          >
            <DSTypography variant="h4">
              {collectionName || 'Untitled'}
            </DSTypography>
            <DSBox
              sx={{
                display: 'flex',
                gap: spacing(2),
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <DSTypography variant="body2" color="text.secondary">
                {`Last Update ${new Date(updatedAt)
                  .toLocaleDateString(undefined, {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                  })
                  .replace(/\//g, '/')}`}
              </DSTypography>
              <CollectionStatus
                state={CollectionReviewStates.Draft}
                reviewMessage={reviewMessage}
                comment={reviewComment}
                onCommentChange={setReviewComment}
              />
            </DSBox>
          </DSBox>
          <DSBox
            sx={{
              display: 'flex',
              gap: spacing(2),
              flexDirection: 'row',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <DSTooltip
              title={
                isMissingArtistName
                  ? 'Add your artist name on the Artist page before previewing the product page.'
                  : ''
              }
            >
              <DSSecondaryButton
                variant="contained"
                startIcon={
                  isFetchingPreviewUrl ? (
                    <DSCircularProgress size={20} />
                  ) : (
                    <EyeIcon />
                  )
                }
                onClick={handleCreateShopifyPreviewUrl}
                disabled={
                  isFetchingPreviewUrl ||
                  !canPreviewProductPage ||
                  isMissingArtistName
                }
              >
                Product Page
              </DSSecondaryButton>
            </DSTooltip>
            {isInMarketplace && collectionId && (
              <AffiliateLinkButton
                collectionId={collectionId}
                collectionName={collectionName || 'Untitled'}
                hasAffiliateLink={hasAffiliateLink}
                cartImage={bundleCartImage}
              />
            )}
            <DSTooltip title={handleMissingFields} placement="bottom">
              <DSPrimaryButton
                disabled={isSubmitDisabled}
                onClick={handlePublishClick}
                startIcon={<MarketplaceIcon />}
              >
                Marketplace
              </DSPrimaryButton>
            </DSTooltip>
          </DSBox>
        </DSBox>
        <DSBox
          sx={{
            width: '100%',
            gap: spacing(1),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DSBox
            sx={{
              display: 'flex',
              gap: 0,
              flexDirection: 'row',
              marginTop: spacing(2),
              width: '100%',
              alignItems: 'center',
            }}
          >
            {['Content', 'Assets', 'Monetization Models'].map((tab, index) => (
              <DSTabButton
                key={tab}
                title={tab}
                isSelected={activeTab === index}
                onClick={() => handleTabChange(index)}
                sx={{ flex: 1 }}
              />
            ))}
          </DSBox>
          <DSDivider
            sx={{
              borderWidth: '1px',
              width: '100%',
            }}
          />
        </DSBox>
        {activeTab === 0 && (
          <CollectionContentTab
            handleOpenPickModal={handleOpenPickModal}
            collectionKlydos={collectionKlydos}
            collectionLogo={collectionLogo}
            collectionPendulumLogoSize={collectionPendulumLogoSize ?? undefined}
            isApprovedCollection={isApprovedCollection}
            collectionPendulumLogoType={collectionPendulumLogoType}
            handleRemoveKlydo={handleRemoveKlydo}
            handleKlydoCardClick={handleKlydoCardClick}
            onOrderChange={handleOrderChange}
          />
        )}
        {activeTab === 1 && (
          <CollectionAssetsTab
            collectionName={collectionName}
            setCollectionName={setCollectionName}
            collectionDescription={collectionDescription}
            setCollectionDescription={setCollectionDescription}
            labels={collectionLabels}
            handleAddLabel={handleAddLabel}
            handleRemoveLabel={handleRemoveLabel}
            collectionCover={collectionCover}
            collectionCoverFileName=""
            isUploadingCover={isUploadingCover}
            onCoverFileChange={handleCoverUpload}
            onCoverRemove={handleCoverRemove}
            collectionLogotype={collectionLogotype}
            collectionLogotypeFileName=""
            isUploadingLogotype={isUploadingLogotype}
            onLogotypeFileChange={handleLogotypeUpload}
            onLogotypeRemove={handleLogotypeRemove}
            collectionLogo={collectionLogo}
            collectionLogoFileName=""
            isUploadingLogo={isUploadingLogo}
            onLogoFileChange={handleLogoUpload}
            onLogoRemove={handleLogoRemove}
            collectionThumbnailBackground={collectionThumbnailBackground}
            collectionThumbnailBackgroundFileName=""
            isUploadingThumbnailBackground={isUploadingThumbnailBackground}
            onThumbnailBackgroundFileChange={handleThumbnailBackgroundUpload}
            onThumbnailBackgroundRemove={handleThumbnailBackgroundRemove}
            collectionHoldPendulumLogo={isCollectionHoldPendulumLogo}
            setCollectionHoldPendulumLogo={setIsCollectionHoldPendulumLogo}
            collectionPendulumLogoSize={collectionPendulumLogoSize}
            setCollectionPendulumLogoSize={setCollectionPendulumLogoSize}
            collectionPendulumLogoType={collectionPendulumLogoType}
            setCollectionPendulumLogoType={setCollectionPendulumLogoType}
            isApprovedCollection={isApprovedCollection}
            collectionThumbnailContrastColor={collectionThumbnailContrastColor}
            setCollectionThumbnailContrastColor={
              setCollectionThumbnailContrastColor
            }
            collectionKlydos={collectionKlydos}
            collectionThumbnailKlydos={collectionThumbnailKlydos}
            setCollectionThumbnailKlydos={setCollectionThumbnailKlydos}
            collectionMockTemplateId={collectionMockTemplateId}
            setCollectionMockTemplateId={setCollectionMockTemplateId}
            collectionCredits={collectionCredits}
            setCollectionCredits={setCollectionCredits}
          />
        )}
        {activeTab === 2 && (
          <CollectionMonetizationTab
            selectedPlanId={selectedPlanId}
            assignedPlanId={user?.metadata?.defaultPlanId ?? null}
            onSelectPlan={setSelectedPlanId}
          />
        )}
      </DSTabGradientViewPaper>
      <SelectKlydosModal
        isOpen={isPickModalOpen}
        onClose={handleClosePickModal}
        onConfirm={handlePickKlydos}
        currentSelectedKlydos={collectionKlydos || []}
        collectionId={collectionId}
        title="Add a Klydo"
        subtitle="Click to choose the Klydo you want to add to your collection"
        confirmButtonText="Add"
      />
      <DeleteDialog
        deleteMessage={DELETE_COLLECTION_ALERT}
        isDeleteDialogOpen={!!deletedCollectionId}
        handleClose={() => setDeletedCollectionId('')}
        handleConfirmationClick={handleDeleteCollection}
        isDeleting={isDeleting}
      />
      <ErrorDialog
        errorTitle="Please complete your profile"
        errorReason={'Profile is missing some required fields'}
        isErrorDialogOpen={profileIncompleteErrorDialogOpen}
        handleClose={() => setProfileIncompleteErrorDialogOpen(false)}
      />
      <WarningDialog
        warningMessage={warningMessage}
        open={warningDialogOpen}
        handleConfirmationClick={handleWarningConfirm}
        handleClose={handleWarningClose}
        isConfirmDisabled={isNameTaken}
        warningNote={nameWarningNote}
      />
    </DSBox>
  );
};

export default CreateCollectionContainer;
